import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {SelectComboboxList, SelectItem, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useGetInfiniteUsers} from '@/features/users/queries/useGetInfiniteUsers'
import {UserSmall} from '@/features/users/types'
import {useTranslation} from '@/translations/i18n'
import {TableFilterKey} from '@/types'
import {useMemo} from 'react'

export const InputSelectUsersList = ({
    disabled,
    isQueryEnabled,
    disabledOptions,
    search,
    filters
}: {
    disabled: boolean
    isQueryEnabled: boolean
    disabledOptions: string[]
    search?: string
    filters?: Partial<Record<TableFilterKey, unknown>>
}) => {
    const {t} = useTranslation()
    const {remappedData, isLoading, fetchNextPage, hasNextPage, isError, isFetchingNextPage} = useGetInfiniteUsers(
        {
            limit: 20,
            search,
            filters
        },
        {enabled: !disabled && isQueryEnabled}
    )

    // Set of disabled options
    const disabledSet = useMemo(() => new Set(disabledOptions), [disabledOptions])

    // Item content renderer
    const itemContent = useMemo(() => {
        return (itemIndex: number, item: UserSmall) => (
            <SelectItem hasItemCheckbox key={itemIndex} value={item.id} disabled={disabledSet.has(item.id)}>
                {item.fullName}
                {item.specializationName && (
                    <span>
                        {` `}({item.specializationName})
                    </span>
                )}
            </SelectItem>
        )
    }, [disabledSet])

    return (
        <SelectComboboxList>
            {isLoading ? (
                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
            ) : isError ? (
                <InputHelpText error={t('errors:default')} />
            ) : remappedData.length > 0 ? (
                <SelectVirtualList
                    data={remappedData}
                    totalCount={remappedData.length}
                    overscan={100}
                    endReached={() => hasNextPage && fetchNextPage()}
                    itemContent={itemContent}
                    components={{
                        Footer: () =>
                            isFetchingNextPage &&
                            Array.from({length: 4}, (_, index) => <Skeleton key={index} height={32} />)
                    }}
                />
            ) : (
                <InputHelpText helpText={t('commons:no_results')} />
            )}
        </SelectComboboxList>
    )
}
